import { f7 } from 'framework7-react';
import { FC, useRef } from 'react';
import style from './AICommentCardStyle.module.css';
import { deliverProactiveMessageApi } from '@/api/aiChatApi';
import { paths } from '@/config/paths';
import { ProactiveMessage } from '@/types/api/aiChat';

interface AICommentCardProps {
  message: ProactiveMessage;
  onActionClick?: () => void;
}

/**
 * AIからのコメントカード
 * ホーム画面トップに表示される、AIからの能動的なメッセージを表示するカード
 */
export const AICommentCard: FC<AICommentCardProps> = ({
  message,
  onActionClick,
}) => {
  // 連打でチャットを二重に開かないための門。
  // 声がけの二重投入自体はサーバ側で防いでいる
  const isOpeningRef = useRef(false);

  const handleActionClick = async () => {
    if (onActionClick) {
      onActionClick();
      return;
    }
    if (message.action?.type === 'open_chat') {
      if (isOpeningRef.current) {
        return;
      }
      isOpeningRef.current = true;
      // 投入の往復で一瞬無反応になるため、待っていることを見せる
      f7.preloader.show();

      try {
        // 遷移より先に投入する。あとで投げると履歴取得に間に合わず、
        // 声がけがチャットの1件目に出ない
        await deliverProactiveMessageApi();
      } catch {
        // 投入できなくてもチャットは開く。声がけが履歴に残らないだけ
      } finally {
        f7.preloader.hide();
        isOpeningRef.current = false;
      }

      f7.view.main.router.navigate(paths.aiChat);
    } else if (message.action?.type === 'open_subscription_payment') {
      // 優先度1（サブスク決済登録未完）の遷移先。
      // 未対応だとカードをタップしても何も起きなかった。
      f7.view.main.router.navigate(paths.subscriptionPayment);
    } else if (message.action?.type === 'navigate' && message.action?.path) {
      f7.view.main.router.navigate(message.action.path);
    }
  };

  if (!message.has_message || !message.message) {
    return null;
  }

  return (
    <div
      className={style['ai-comment-card']}
      onClick={() => void handleActionClick()}
    >
      <div className={style['ai-comment-header']}>
        <div className={style['ai-icon-wrapper']}>
          <i className={`icon-bulb ${style['ai-icon']}`} />
        </div>
        <span className={style['ai-label']}>AIからのコメント</span>
      </div>
      <div className={style['ai-comment-body']}>
        <p className={style['ai-message']}>{message.message}</p>
      </div>
      {message.action && (
        <div className={style['ai-comment-footer']}>
          <span className={style['action-label']}>{message.action.label}</span>
          <i className={`icon-right ${style['action-icon']}`} />
        </div>
      )}
    </div>
  );
};
